import { useQuery } from "@tanstack/react-query";
import {
  type CandlestickData,
  ColorType,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type SeriesMarker,
  createChart,
  type Time,
} from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Candle, Signal } from "@ct/shared-types";
import { fetchCandles, fetchRecentSignals } from "../../lib/api.js";
import { marketSocket } from "../../lib/ws.js";

interface Props {
  exchange: string;
  symbol: string;
  timeframe: string;
}

interface IndicatorPayload {
  exchange: string;
  symbol: string;
  timeframe: string;
  time: number;
  snapshot: {
    ema21: number | null;
    ema50: number | null;
    bb: { upper: number; middle: number; lower: number } | null;
  };
}

const toCandlestick = (c: Candle): CandlestickData => ({
  time: Math.floor(c.openTime / 1000) as Time,
  open: c.open,
  high: c.high,
  low: c.low,
  close: c.close,
});

const linePoint = (time: number, value: number): LineData => ({
  time: Math.floor(time / 1000) as Time,
  value,
});

export function CandleChart({ exchange, symbol, timeframe }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const ema21Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const ema50Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const bbUpperRef = useRef<ISeriesApi<"Line"> | null>(null);
  const bbLowerRef = useRef<ISeriesApi<"Line"> | null>(null);
  const [signals, setSignals] = useState<Signal[]>([]);

  const history = useQuery({
    queryKey: ["candles", exchange, symbol, timeframe],
    queryFn: () => fetchCandles({ exchange, symbol, timeframe, limit: 500 }),
  });

  const initialSignals = useQuery({
    queryKey: ["chart-signals", exchange, symbol, timeframe],
    queryFn: () => fetchRecentSignals(200),
  });

  useEffect(() => {
    if (initialSignals.data) {
      setSignals(
        initialSignals.data.filter(
          (s) =>
            s.exchange === exchange &&
            s.symbol === symbol &&
            s.timeframe === timeframe,
        ),
      );
    }
  }, [initialSignals.data, exchange, symbol, timeframe]);

  // Initialize chart once.
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#0a0a0b" },
        textColor: "#a1a1aa",
      },
      grid: {
        vertLines: { color: "#1f1f23" },
        horzLines: { color: "#1f1f23" },
      },
      crosshair: { mode: 0 },
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    candleSeriesRef.current = chart.addCandlestickSeries({
      upColor: "#3aed8a",
      downColor: "#ff4d6d",
      wickUpColor: "#3aed8a",
      wickDownColor: "#ff4d6d",
      borderVisible: false,
    });
    ema21Ref.current = chart.addLineSeries({
      color: "#fbbf24",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    ema50Ref.current = chart.addLineSeries({
      color: "#60a5fa",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    bbUpperRef.current = chart.addLineSeries({
      color: "rgba(168,85,247,0.45)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    bbLowerRef.current = chart.addLineSeries({
      color: "rgba(168,85,247,0.45)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    chartRef.current = chart;
    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      ema21Ref.current = null;
      ema50Ref.current = null;
      bbUpperRef.current = null;
      bbLowerRef.current = null;
    };
  }, []);

  // Set candle history when fetched.
  useEffect(() => {
    if (!candleSeriesRef.current || !history.data) return;
    candleSeriesRef.current.setData(history.data.map(toCandlestick));
  }, [history.data]);

  // Live candle updates.
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    const channel = `md:candle:${exchange}:${symbol}:${timeframe}`;
    const unsub = marketSocket.subscribe(channel, (_ch, payload) => {
      const c = payload as Candle;
      if (
        c.exchange !== exchange ||
        c.symbol !== symbol ||
        c.timeframe !== timeframe
      ) {
        return;
      }
      candleSeriesRef.current?.update(toCandlestick(c));
    });
    return unsub;
  }, [exchange, symbol, timeframe]);

  // Live indicator overlay updates.
  useEffect(() => {
    const channel = `ind:${exchange}:${symbol}:${timeframe}`;
    const unsub = marketSocket.subscribe(channel, (_ch, payload) => {
      const p = payload as IndicatorPayload;
      if (!p?.snapshot) return;
      if (p.snapshot.ema21 !== null && ema21Ref.current) {
        ema21Ref.current.update(linePoint(p.time, p.snapshot.ema21));
      }
      if (p.snapshot.ema50 !== null && ema50Ref.current) {
        ema50Ref.current.update(linePoint(p.time, p.snapshot.ema50));
      }
      if (p.snapshot.bb && bbUpperRef.current && bbLowerRef.current) {
        bbUpperRef.current.update(linePoint(p.time, p.snapshot.bb.upper));
        bbLowerRef.current.update(linePoint(p.time, p.snapshot.bb.lower));
      }
    });
    return unsub;
  }, [exchange, symbol, timeframe]);

  // Live signal stream (filtered to current symbol).
  useEffect(() => {
    return marketSocket.subscribe("signals:all", (_c, payload) => {
      const s = payload as Signal;
      if (
        s.exchange !== exchange ||
        s.symbol !== symbol ||
        s.timeframe !== timeframe
      ) {
        return;
      }
      setSignals((prev) =>
        prev.some((p) => p.id === s.id) ? prev : [s, ...prev].slice(0, 200),
      );
    });
  }, [exchange, symbol, timeframe]);

  // Render entry markers and SL/TP price lines for the most recent active signal.
  const markers = useMemo<SeriesMarker<Time>[]>(
    () =>
      signals.map((s) => ({
        time: Math.floor(s.issuedAt / 1000) as Time,
        position: "belowBar" as const,
        color: "#A855F7",
        shape: "arrowUp" as const,
        text: `${s.strategyId} ${(s.confidence * 100).toFixed(0)}%`,
      })),
    [signals],
  );

  useEffect(() => {
    if (!candleSeriesRef.current) return;
    candleSeriesRef.current.setMarkers(markers);
  }, [markers]);

  // Apply price lines for the latest active (non-expired) signal.
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    const now = Date.now();
    const active = signals.find((s) => s.expiresAt > now);
    const lines: ReturnType<typeof series.createPriceLine>[] = [];
    if (active) {
      lines.push(
        series.createPriceLine({
          price: active.entryPrice,
          color: "#22C55E",
          lineWidth: 1,
          lineStyle: 0,
          axisLabelVisible: true,
          title: "ENTRY",
        }),
        series.createPriceLine({
          price: active.stopLoss,
          color: "#ff4d6d",
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: "SL",
        }),
      );
      for (const tp of [active.takeProfit1, active.takeProfit2, active.takeProfit3]) {
        if (tp == null) continue;
        lines.push(
          series.createPriceLine({
            price: tp,
            color: "#3aed8a",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "TP",
          }),
        );
      }
    }
    return () => {
      for (const l of lines) series.removePriceLine(l);
    };
  }, [signals]);

  const noData =
    !history.isFetching && history.isSuccess && (history.data?.length ?? 0) === 0;

  return (
    <div className="flex h-full min-h-0 w-full flex-col" data-testid="candle-chart">
      <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-1 text-xs text-zinc-400">
        <span className="font-mono">
          {exchange} · {symbol} · {timeframe}
        </span>
        <span className="flex items-center gap-3">
          <span className="text-amber-400">EMA21</span>
          <span className="text-blue-400">EMA50</span>
          <span className="text-purple-400">BB</span>
          {history.isFetching && <span className="text-zinc-500">loading…</span>}
          {history.isError && <span className="text-red-400">fetch error</span>}
        </span>
      </div>
      <div className="relative min-h-0 flex-1">
        <div ref={containerRef} data-testid="chart-canvas-container" className="absolute inset-0" />
        {noData && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-950/80">
            <svg
              className="h-10 w-10 text-zinc-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.5l4-4 4 4 4-7 4 4"
              />
            </svg>
            <p className="text-sm font-medium text-zinc-400">No chart data</p>
            <p className="text-xs text-zinc-600">
              {symbol} is being ingested — live candles will appear shortly
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
